import { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styled from '@emotion/styled';
import { tokens, Text } from '@hotjar/hotsauce';

import { RemovalConfirmation } from '@hotjar/comments/components/RemovalConfirmation';
import { Thread } from '@hotjar/comments/components/Thread';
import { useComments } from '@hotjar/comments/hooks/useComments';
import { invalidateQuery } from '@hotjar/webapp/features/Highlights/util';
import type {
  Thread as ThreadModel,
  Comment as CommentModel,
  Reply,
} from '@hotjar/comments/domains/model';
import type { OrganizationId, OrganizationMember } from '@hotjar/organization';
import { ErrorBoundary } from '@hotjar/error-handler';

const Wrapper = styled.section`
  background-color: ${tokens.color.ui_background};
  padding: ${tokens.spacer.x2} ${tokens.spacer.x2};
  padding-bottom: 0;
`;

const Header = styled(Text)`
  margin: 0;
  margin-bottom: ${tokens.spacer.x1};
`;

const NewThreadWrapper = styled.div`
  padding-bottom: ${tokens.spacer.x3};
`;

export interface ResponseCommentsProps {
  autoFocusTextField?: boolean;
  className?: string;
  currentUser: OrganizationMember;
  onCommentAdded: (comment: CommentModel) => void;
  onCommentRemoved?: (comment: CommentModel, wasLastAuthorComment: boolean) => void;
  organizationId: OrganizationId;
  responseId: number;
  siteId: string;
  widgetId: number;
}

export const ResponseComments = ({
  autoFocusTextField = false,
  className,
  currentUser,
  onCommentAdded,
  onCommentRemoved,
  organizationId,
  responseId,
  siteId,
  widgetId,
}: ResponseCommentsProps): JSX.Element => {
  const { t } = useTranslation();
  const [firstThread, setFirstThread] = useState<ThreadModel | null>(null);

  function isAuthorInThreads(threads: ThreadModel[], authorId: number) {
    return threads.some(
      (thread) =>
        thread.author.id === authorId ||
        thread.replies.some((reply) => reply.author.id === authorId),
    );
  }

  function removeReply(threads: ThreadModel[], reply: Reply) {
    const newThreads = threads.map((oldThread) =>
      oldThread.id !== reply.threadId
        ? oldThread
        : {
            ...oldThread,
            replies: oldThread.replies.filter((oldReply) => oldReply.id !== reply.id),
          },
    );

    onCommentRemoved?.(reply, !isAuthorInThreads(newThreads, reply.author.id));

    return newThreads;
  }

  const { threads, threadProps, removalConfirmationProps, renderNewThread } = useComments({
    siteId,
    organizationId,
    widgetId,
    widgetType: 'incoming_feedback',
    scopeId: responseId,
    currentUser,
    apiToken: null,
    onThreadAdded: (thread) => {
      setFirstThread(thread);
      onCommentAdded(thread);
    },
    onThreadRemoved: (thread) => {
      setFirstThread(null);

      /*
        removing a thread should raise onCommentRemoved for each reply AND for the thread itself
        wasLastAuthorComment should only be set to true when the last comment of an author is removed
      */
      const newThreads = thread.replies
        .reduce<ThreadModel[]>(
          (previousThreads, reply) => removeReply(previousThreads, reply),
          threads,
        )
        .filter((oldThread) => oldThread.id !== thread.id);

      onCommentRemoved?.(thread, !isAuthorInThreads(newThreads, thread.author.id));
    },
    onReplyAdded: onCommentAdded,
    onReplyRemoved: (reply) => {
      removeReply(threads, reply);
    },
    newThreadMetadata: {},
    compactNewThread: true,
    autoFocusTextField,
    setActiveItem: () => null,
    invalidateQuery,
  });

  useLayoutEffect(() => {
    setFirstThread(threads[0] ?? null);
  }, [threads]);

  return (
    <ErrorBoundary squadSentryTag="workflow">
      <Wrapper className={className}>
        <Header as="p" textType="subtitle2">
          {t('comment:view.title')}
        </Header>
        {firstThread === null ? (
          <NewThreadWrapper>{renderNewThread()}</NewThreadWrapper>
        ) : (
          <Thread {...threadProps} thread={firstThread} scrollable={false} />
        )}

        <RemovalConfirmation {...removalConfirmationProps} />
      </Wrapper>
    </ErrorBoundary>
  );
};
